"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreemshotController = void 0;
const analytics_1 = require("@streem/analytics");
const api_1 = require("@streem/api");
const domain_id_1 = require("@streem/domain-id");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const fetch_download_url_1 = require("../../util/fetch_download_url");
const logger_1 = require("../../util/logger");
const artifact_timed_deletion_1 = require("../../util/rx/artifact_timed_deletion");
const filter_empty_array_1 = require("../../util/rx/filter_empty_array");
const first_from_group_1 = require("../../util/rx/first_from_group");
const utils_1 = require("../wall/utils");
class StreemshotController {
    constructor(config) {
        var _a;
        this.config = config;
        this.log = logger_1.logger.extend('sdk:streemshot_controller');
        this.storageCache = {};
        this.requests = ((_a = this.config.context) === null || _a === void 0 ? void 0 : _a.localUserId)
            ? this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('streemshotRequest'), (0, utils_1.accumulateAndMapWallEvents)('streemshotRequest'), this.requestedOf(this.config.context.localUserId), (0, operators_1.map)(requests => requests.reverse()))
            : new rxjs_1.Observable();
        this.responses = this.config.roomWall.itemsByType.pipe((0, operators_1.filter)(g => g.key === 'streemshot' || g.key === 'streemshotRevision'), (0, operators_1.mergeAll)(), (0, operators_1.scan)((acc, cur) => {
            var _a, _b, _c, _d, _e, _f, _g;
            // First we need to make sure there is a key for each streemshot and the value is set to an empty array
            if (cur.itemType === 'streemshot' && !acc[cur.itemId])
                acc[cur.itemId] = [];
            if (cur.itemType === 'streemshotRevision' &&
                cur.type !== 'DELETED' &&
                ((_b = (_a = cur.item) === null || _a === void 0 ? void 0 : _a.streemshotRevision) === null || _b === void 0 ? void 0 : _b.streemshotId) &&
                !acc[(_d = (_c = cur.item.streemshotRevision) === null || _c === void 0 ? void 0 : _c.streemshotId) === null || _d === void 0 ? void 0 : _d.toString()])
                acc[(_f = (_e = cur.item.streemshotRevision) === null || _e === void 0 ? void 0 : _e.streemshotId) === null || _f === void 0 ? void 0 : _f.toString()] = [];
            if (cur.type === 'DELETED') {
                delete acc[new domain_id_1.StreemshotId(cur.itemId).toString()];
                return acc;
            }
            // [STREEM-11612] Some old wall streemshots come in as semi-UUID (ss_9f882020-1aea-11eb-8acc-69ae09918942) instead of SID (ss_4r1zLgnsKQ1yhSbmAKgnsw)
            // Convert them all to SID standard to ensure compatability
            let streemshotId;
            switch (cur.itemType) {
                case 'streemshot':
                    streemshotId = new domain_id_1.StreemshotId(cur.itemId);
                    break;
                case 'streemshotRevision':
                    (0, toolbox_1.invariant)((_g = cur.item.streemshotRevision) === null || _g === void 0 ? void 0 : _g.streemshotId, 'Invalid streemshotRevision: could not find attached streemshotId');
                    streemshotId = new domain_id_1.StreemshotId(cur.item.streemshotRevision.streemshotId);
                    break;
                default:
                    throw new Error(`Attempted to treat ${cur.itemType} as a streemshot`);
            }
            (0, toolbox_1.invariant)(streemshotId, `Attempted to update a ${cur.itemType} without a streemshot ID`);
            switch (cur.itemType) {
                case 'streemshot': {
                    // [STREEM-11684] Android sometimes sends the revision before the associated streemshot
                    // If there exists a revision, only append new data (don't overwrite newer data)
                    const baseStreemshot = (0, utils_1.mapWithMetadata)(cur.item, cur.itemType);
                    acc[streemshotId.toString()].push(baseStreemshot);
                    break;
                }
                case 'streemshotRevision': {
                    acc[streemshotId.toString()].push(Object.assign(Object.assign({}, cur.item), cur.item.streemshotRevision));
                    break;
                }
            }
            return acc;
        }, {}), (0, operators_1.debounceTime)(250), // add a 250ms delay in case of an immediate revision (solves flashing image)
        (0, operators_1.mergeMap)((acc) => __awaiter(this, void 0, void 0, function* () {
            const streemshots = Object.values(acc);
            // map over streemshot wall items and use the `storageUrl` to fetch a presigned download url
            const streemshotDownloadPromises = yield Promise.allSettled(streemshots.map((ss) => __awaiter(this, void 0, void 0, function* () {
                let latestRevision;
                if (ss.length > 1) {
                    ss.sort((a, b) => {
                        var _a, _b, _c, _d, _e, _f;
                        return ((_c = (_b = (_a = a.clientCreatedAt) === null || _a === void 0 ? void 0 : _a.seconds) === null || _b === void 0 ? void 0 : _b.toNumber()) !== null && _c !== void 0 ? _c : 0) -
                            ((_f = (_e = (_d = b.clientCreatedAt) === null || _d === void 0 ? void 0 : _d.seconds) === null || _e === void 0 ? void 0 : _e.toNumber()) !== null && _f !== void 0 ? _f : 0);
                    });
                    latestRevision = ss[ss.length - 1];
                    // show the timestamp for the original streemshot so that it appears corresponds accurately to the video
                    const originalIndex = ss.findIndex(item => !item.streemshotId);
                    latestRevision.clientCreatedAt = ss[originalIndex].clientCreatedAt;
                }
                else {
                    latestRevision = ss[0];
                }
                (0, toolbox_1.invariant)(latestRevision.storageUrl, 'Invalid streemshot: could not find attached storageUrl');
                const cacheMiss = !this.storageCache[latestRevision.storageUrl];
                if (cacheMiss) {
                    this.storageCache[latestRevision.storageUrl] = yield (0, fetch_download_url_1.fetchDownloadUrl)(this.config.context.roomId, latestRevision.storageUrl);
                }
                latestRevision.downloadUrl = this.storageCache[latestRevision.storageUrl];
                return latestRevision;
            })));
            const processedStreemshots = streemshotDownloadPromises
                .filter(p => p.status === 'fulfilled')
                .map((p) => p.value);
            return (processedStreemshots
                // Filter out errors
                .filter(ss => !!ss.downloadUrl)
                .sort((a, b) => {
                var _a, _b, _c, _d, _e, _f;
                return ((_c = (_b = (_a = b.clientCreatedAt) === null || _a === void 0 ? void 0 : _a.seconds) === null || _b === void 0 ? void 0 : _b.toNumber()) !== null && _c !== void 0 ? _c : 0) -
                    ((_f = (_e = (_d = a.clientCreatedAt) === null || _d === void 0 ? void 0 : _d.seconds) === null || _e === void 0 ? void 0 : _e.toNumber()) !== null && _f !== void 0 ? _f : 0);
            }));
        }), 1), (0, operators_1.shareReplay)(1));
        this.latestRequest = this.requests.pipe((0, filter_empty_array_1.filterEmptyArray)(), (0, first_from_group_1.firstFromGroup)());
        this.latestResponse = this.responses.pipe((0, filter_empty_array_1.filterEmptyArray)(), (0, first_from_group_1.firstFromGroup)());
    }
    /**
     * Adds a streemshot request to the room wall
     */
    requestStreemshot() {
        (0, toolbox_1.invariant)(this.config.context.remoteUserId && this.config.context.remoteDeviceId, 'remoteUserId and remoteDeviceId are required for streemshot request');
        const artifactId = this.config.roomWall.addStreemshotRequest(this.config.context.remoteUserId, this.config.context.remoteDeviceId);
        (0, analytics_1.recordArtifactRequested)(artifactId);
        this.log.info(`Requesting streemshot from remoteUserId ${this.config.context.remoteUserId} on device ${this.config.context.remoteDeviceId}`, artifactId);
        return artifactId;
    }
    uploadStreemshot(blob, id) {
        return __awaiter(this, void 0, void 0, function* () {
            let signedUrl;
            try {
                signedUrl = yield api_1.StreemAPI.uploads.createSignedUploadUrl({
                    type: api_1.StreemApiCreateSignedUploadUrlRequestType.STREEMSHOT,
                    group: this.config.context.roomId.toString(),
                    sid: id === null || id === void 0 ? void 0 : id.toString(),
                    filename: `frame_${Date.now()}.jpg`,
                });
            }
            catch (error) {
                this.log.error(`failed to create a signed upload url for request ${id}:`, error);
                throw error;
            }
            (0, toolbox_1.invariant)(signedUrl.uploadUrl, 'An upload url must be defined to upload a streemshot');
            // Upload the streemshot.
            try {
                const headers = new Headers();
                headers.set('Content-Type', blob.type);
                headers.set('Content-Length', blob.size.toString());
                yield fetch(signedUrl.uploadUrl, {
                    headers: headers,
                    method: 'PUT',
                    mode: 'cors',
                    body: blob,
                });
                this.log.info(`successfully uploaded streemshot for room ${this.config.context.roomId} in response to request ${id}`);
            }
            catch (error) {
                this.log.error(`failed to upload streemshot to ${signedUrl.uploadUrl}: `, error);
                throw error;
            }
            return signedUrl;
        });
    }
    saveEditedStreemshot(blob, id) {
        return __awaiter(this, void 0, void 0, function* () {
            const newId = new domain_id_1.StreemshotRevisionId();
            const signedUrl = yield this.uploadStreemshot(blob, newId);
            // Add streemshotRevision wall item
            if (signedUrl) {
                const wallItemId = this.config.roomWall.addStreemshotRevision(newId, {
                    storageUrl: signedUrl.storageUrl,
                    downloadUrl: null,
                    streemshotId: id,
                    edited: true,
                });
                this.log.info(`added streemshot revision ${wallItemId} to wall`);
            }
        });
    }
    revertEditedStreemshot(originalStreemshotId) {
        this.config.roomWall.itemsByType
            .pipe((0, utils_1.findInGroup)('streemshot'), (0, utils_1.accumulateAndMapWallEvents)('streemshot'), (0, utils_1.filterByProperty)('id', originalStreemshotId), (0, operators_1.take)(1))
            .subscribe(streemshot => {
            const newId = new domain_id_1.StreemshotRevisionId();
            const wallItemId = this.config.roomWall.addStreemshotRevision(newId, {
                storageUrl: streemshot[0].storageUrl,
                downloadUrl: null,
                streemshotId: streemshot[0].id,
            });
            this.log.info(`reverted streemshot revision and added new revision ${wallItemId} to wall`);
        });
    }
    fulfillStreemshot(streemshotRequestId, streemshotId, payload, imageDimensions) {
        return __awaiter(this, void 0, void 0, function* () {
            const signedUrl = yield this.uploadStreemshot(payload, streemshotId);
            // Add streemshot wall item
            if (signedUrl) {
                const wallItemId = this.config.roomWall.addStreemshotResponse(streemshotId, {
                    inResponseToWallItemId: streemshotRequestId.toString(),
                    storageUrl: signedUrl.storageUrl,
                    imageDimensions: imageDimensions,
                    cameraIntrinsics: null,
                    downloadUrl: null,
                    pose: null,
                    featurePoints: null,
                });
                try {
                    (0, analytics_1.recordArtifactCaptured)(wallItemId);
                }
                catch (error) {
                    // swallow errors
                }
                this.log.info(`added streemshot response ${wallItemId} to wall`);
            }
        });
    }
    /**
     * Get a Streemshot by ID
     * @param id Streemshot ID
     */
    getStreemshot(id) {
        return this.responses.pipe((0, utils_1.filterByProperty)('id', id.toString()), (0, first_from_group_1.firstFromGroup)());
    }
    /**
     * Deletes a streemshot off of the backend
     * @param id The streemshot id to delete
     */
    deleteStreemshot(id) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield api_1.StreemAPI.rooms.deleteStreemshot(this.config.context.roomId.toString(), id.toString());
                yield (0, artifact_timed_deletion_1.artifactTimedDeletion)(id.toString(), this.responses);
                (0, analytics_1.recordArtifactDeleted)({
                    artifactSid: id.toString(),
                    roomSid: this.config.context.roomId.toString(),
                    streemshot: {},
                });
            }
            catch (e) {
                this.log.error('Failed to delete streemshot', id.toString());
            }
        });
    }
    /**
     * Filters streemshot requests by who is requested to **provide** the streemshot
     * @param userId the user who should provide the streemshot
     */
    requestedOf(userId) {
        return (0, operators_1.map)((requests) => requests.filter(request => request.requestedOfUserId === userId.toString()));
    }
}
exports.StreemshotController = StreemshotController;
//# sourceMappingURL=streemshot_controller.js.map